import {isSpan} from '@portabletext/schema'
import type {EditorActor} from '../../../../editor/editor-machine'
import {getNode} from '../../../../node-traversal/get-node'
import {getNodes} from '../../../../node-traversal/get-nodes'
import {getSpanNode} from '../../../../node-traversal/get-span-node'
import {DOMEditor} from '../../../dom/plugin/dom-editor'
import {
  applyStringDiff,
  mergeStringDiffs,
  normalizePoint,
  normalizeRange,
  normalizeStringDiff,
  targetRange,
  verifyDiffState,
  type StringDiff,
  type TextDiff,
} from '../../../dom/utils/diff-text'
import {isDOMSelection, isTrackedMutation} from '../../../dom/utils/dom'
import {after} from '../../../editor/after'
import {range as editorRange} from '../../../editor/range'
import {rangeRef} from '../../../editor/range-ref'
import type {Editor} from '../../../interfaces/editor'
import type {Path} from '../../../interfaces/path'
import type {Point} from '../../../interfaces/point'
import type {Range} from '../../../interfaces/range'
import {isVoidNode} from '../../../node/is-void-node'
import {pathEquals} from '../../../path/path-equals'
import {isPoint} from '../../../point/is-point'
import {isCollapsedRange} from '../../../range/is-collapsed-range'
import {isExpandedRange} from '../../../range/is-expanded-range'
import {rangeEdges} from '../../../range/range-edges'
import {rangeEquals} from '../../../range/range-equals'
import type {DebouncedFunc} from '../../utils/debounce'

// https://github.com/facebook/draft-js/blob/main/src/component/handlers/composition/DraftEditorCompositionHandler.js#L41
// When using keyboard English association function, conpositionEnd triggered too fast, resulting in after `insertText` still maintain association state.
const RESOLVE_DELAY = 25

// Time with no user interaction before the current user action is considered as done.
const FLUSH_DELAY = 200

// Replace with `const debug = console.log` to debug
const debug = (..._: unknown[]) => {}

// Type guard to check if a value is a DataTransfer
const isDataTransfer = (value: any): value is DataTransfer =>
  value?.constructor.name === 'DataTransfer'

export type CreateAndroidInputManagerOptions = {
  editor: Editor
  editorActor: EditorActor

  scheduleOnDOMSelectionChange: DebouncedFunc<() => void>
  onDOMSelectionChange: DebouncedFunc<() => void>
}

export type AndroidInputManager = {
  flush: () => void
  scheduleFlush: () => void

  hasPendingDiffs: () => boolean
  hasPendingAction: () => boolean
  hasPendingChanges: () => boolean
  isFlushing: () => boolean | 'action'

  handleUserSelect: (range: Range | null) => void
  handleCompositionEnd: (event: React.CompositionEvent<HTMLDivElement>) => void
  handleCompositionStart: (
    event: React.CompositionEvent<HTMLDivElement>,
  ) => void
  handleDOMBeforeInput: (event: InputEvent) => void
  handleKeyDown: (event: React.KeyboardEvent<HTMLDivElement>) => void

  handleDomMutations: (mutations: MutationRecord[]) => void
  handleInput: () => void
}

export function createAndroidInputManager({
  editor,
  editorActor,
  scheduleOnDOMSelectionChange,
  onDOMSelectionChange,
}: CreateAndroidInputManagerOptions): AndroidInputManager {
  let flushing: 'action' | boolean = false
  let compositionEndTimeoutId: ReturnType<typeof setTimeout> | null = null
  let flushTimeoutId: ReturnType<typeof setTimeout> | null = null
  let actionTimeoutId: ReturnType<typeof setTimeout> | null = null

  let idCounter = 0
  let insertPositionHint: StringDiff | null | false = false

  const applyPendingSelection = () => {
    const pendingSelection = editor.pendingSelection
    editor.pendingSelection = null

    if (pendingSelection) {
      const {selection} = editor
      const normalized = normalizeRange(editor, pendingSelection)

      debug('apply pending selection', pendingSelection, normalized)

      if (normalized && (!selection || !rangeEquals(normalized, selection))) {
        editor.select(normalized)
      }
    }
  }

  const performAction = () => {
    const action = editor.pendingAction
    editor.pendingAction = null
    if (!action) {
      return
    }

    if (action.at) {
      const target = isPoint(action.at)
        ? normalizePoint(editor, action.at)
        : normalizeRange(editor, action.at)

      if (!target) {
        return
      }

      const targetRange = editorRange(editor, target)
      if (!editor.selection || !rangeEquals(editor.selection, targetRange)) {
        editor.select(target)
      }
    }

    action.run()
  }

  const flush = () => {
    if (flushTimeoutId) {
      clearTimeout(flushTimeoutId)
      flushTimeoutId = null
    }

    if (actionTimeoutId) {
      clearTimeout(actionTimeoutId)
      actionTimeoutId = null
    }

    if (!hasPendingDiffs() && !hasPendingAction()) {
      applyPendingSelection()
      return
    }

    if (!flushing) {
      flushing = true
      // biome-ignore lint/suspicious/noAssignInExpressions: Slate upstream pattern
      setTimeout(() => (flushing = false))
    }

    if (hasPendingAction()) {
      flushing = 'action'
    }

    const selectionRef =
      editor.selection &&
      rangeRef(editor, editor.selection, {affinity: 'forward'})

    debug('flush', editor.pendingAction, editor.pendingDiffs)

    let scheduleSelectionChange = hasPendingDiffs()

    let diff: TextDiff | undefined
    // biome-ignore lint/suspicious/noAssignInExpressions: Slate upstream pattern
    while ((diff = editor.pendingDiffs?.[0])) {
      const range = targetRange(diff)
      if (!editor.selection || !rangeEquals(editor.selection, range)) {
        editor.select(range)
      }

      if (diff.diff.text) {
        editorActor.send({
          type: 'behavior event',
          behaviorEvent: {type: 'insert.text', text: diff.diff.text},
          editor,
        })
      } else {
        editorActor.send({
          type: 'behavior event',
          behaviorEvent: {type: 'delete', direction: 'forward'},
          editor,
        })
      }

      // Remove diff only after we have applied it to account for it when transforming
      // pending ranges.
      // biome-ignore lint/suspicious/noNonNullAssertedOptionalChain: Slate upstream pattern — diffs guaranteed to exist in loop
      editor.pendingDiffs = editor.pendingDiffs?.filter(
        ({id}) => id !== diff!.id,
      )!

      if (!verifyDiffState(editor, diff)) {
        debug('invalid diff state')
        scheduleSelectionChange = false
        editor.pendingAction = null
        flushing = 'action'

        // Ensure we don't restore the pending user (dom) selection
        // since the document and dom state do not match.
        editor.pendingSelection = null
        scheduleOnDOMSelectionChange.cancel()
        onDOMSelectionChange.cancel()
        selectionRef?.unref()
      }
    }

    const selection = selectionRef?.unref()
    if (
      selection &&
      !editor.pendingSelection &&
      (!editor.selection || !rangeEquals(selection, editor.selection))
    ) {
      editor.select(selection)
    }

    if (hasPendingAction()) {
      performAction()
      return
    }

    // COMPAT: The selectionChange event is fired after the action is performed,
    // so we have to manually schedule it to ensure we don't 'throw away' the selection
    // while rendering if we have pending changes.
    if (scheduleSelectionChange) {
      debug('scheduleOnDOMSelectionChange pending changes')
      scheduleOnDOMSelectionChange()
    }

    scheduleOnDOMSelectionChange.flush()
    onDOMSelectionChange.flush()

    applyPendingSelection()
  }

  const handleCompositionEnd = (
    _event: React.CompositionEvent<HTMLDivElement>,
  ) => {
    if (compositionEndTimeoutId) {
      clearTimeout(compositionEndTimeoutId)
    }

    compositionEndTimeoutId = setTimeout(() => {
      editor.composing = false
      flush()
    }, RESOLVE_DELAY)
  }

  const handleCompositionStart = (
    _event: React.CompositionEvent<HTMLDivElement>,
  ) => {
    debug('composition start')

    editor.composing = true

    if (compositionEndTimeoutId) {
      clearTimeout(compositionEndTimeoutId)
      compositionEndTimeoutId = null
    }
  }

  const storeDiff = (path: Path, diff: StringDiff) => {
    debug('storeDiff', path, diff)

    const pendingDiffs = editor.pendingDiffs

    const targetEntry = getSpanNode(editor, path)

    if (!targetEntry) {
      return
    }

    const target = targetEntry.node

    const idx = pendingDiffs.findIndex((change) =>
      pathEquals(change.path, path),
    )
    if (idx < 0) {
      const normalized = normalizeStringDiff(target.text, diff)
      if (normalized) {
        pendingDiffs.push({path, diff, id: idCounter++})
      }

      return
    }

    const merged = mergeStringDiffs(target.text, pendingDiffs[idx]!.diff, diff)
    if (!merged) {
      pendingDiffs.splice(idx, 1)
      return
    }

    pendingDiffs[idx] = {
      ...pendingDiffs[idx]!,
      diff: merged,
    }
  }

  const scheduleAction = (
    run: () => void,
    {at}: {at?: Point | Range} = {},
  ): void => {
    insertPositionHint = false
    debug('scheduleAction', {at, run})

    editor.pendingSelection = null
    scheduleOnDOMSelectionChange.cancel()
    onDOMSelectionChange.cancel()

    if (hasPendingAction()) {
      flush()
    }

    editor.pendingAction = {at, run}

    // COMPAT: When deleting before a non-contenteditable element chrome only fires a beforeinput,
    // (no input) and doesn't perform any dom mutations. Without a flush timeout we would never flush
    // in this case and thus never actually perform the action.
    actionTimeoutId = setTimeout(flush)
  }

  const handleDOMBeforeInput = (event: InputEvent): void => {
    if (flushTimeoutId) {
      clearTimeout(flushTimeoutId)
      flushTimeoutId = null
    }

    if (editor.isNodeMapDirty) {
      return
    }

    const {inputType: type} = event
    let targetRange: Range | null = null
    const data: DataTransfer | string | undefined =
      (event as any).dataTransfer || event.data || undefined

    if (
      insertPositionHint !== false &&
      type !== 'insertText' &&
      type !== 'insertCompositionText'
    ) {
      insertPositionHint = false
    }

    let [nativeTargetRange] = (event as any).getTargetRanges()
    if (nativeTargetRange) {
      targetRange = DOMEditor.toSlateRange(editor, nativeTargetRange, {
        exactMatch: false,
        suppressThrow: true,
      })
    }

    // COMPAT: SelectionChange event is fired after the action is performed, so we
    // have to manually get the selection here to ensure it's up-to-date.
    const window = DOMEditor.getWindow(editor)
    const domSelection = window.getSelection()
    if (!targetRange && domSelection) {
      nativeTargetRange = domSelection
      targetRange = DOMEditor.toSlateRange(editor, domSelection, {
        exactMatch: false,
        suppressThrow: true,
      })
    }

    targetRange = targetRange ?? editor.selection
    if (!targetRange) {
      return
    }

    // By default, the input manager tries to store text diffs so that we can
    // defer flushing them at a later point in time. We don't want to flush
    // for every input event as this can be expensive. However, there are some
    // scenarios where we cannot safely store the text diff and must instead
    // schedule an action to let Slate normalize the editor state.
    let canStoreDiff = true

    if (type.startsWith('delete')) {
      const direction = type.endsWith('Backward') ? 'backward' : 'forward'
      let [start, end] = rangeEdges(targetRange, editor)
      const leafNodeEntry = getNode(editor, start.path)
      const leafResult =
        leafNodeEntry &&
        (isSpan({schema: editor.schema}, leafNodeEntry.node) ||
          isVoidNode(editor, leafNodeEntry.node, start.path))
          ? leafNodeEntry
          : undefined

      if (!leafResult) {
        return scheduleAction(
          () =>
            editorActor.send({
              type: 'behavior event',
              behaviorEvent: {type: 'delete', direction},
              editor,
            }),
          {at: targetRange},
        )
      }

      let leaf = leafResult.node
      let path = leafResult.path

      if (!isSpan({schema: editor.schema}, leaf)) {
        return scheduleAction(
          () =>
            editorActor.send({
              type: 'behavior event',
              behaviorEvent: {type: 'delete', direction},
              editor,
            }),
          {at: targetRange},
        )
      }

      if (isExpandedRange(targetRange)) {
        if (leaf.text.length === start.offset && end.offset === 0) {
          const afterPoint = after(editor, start.path)
          const [nextEntry] = afterPoint
            ? getNodes(editor, {
                from: afterPoint.path,
                match: (n) => isSpan({schema: editor.schema}, n),
              })
            : []
          if (
            nextEntry &&
            isSpan({schema: editor.schema}, nextEntry.node) &&
            pathEquals(nextEntry.path, end.path)
          ) {
            // when deleting a linebreak, targetRange will span across the break (ie start in the node before and end in the node after)
            // if the node before is empty, this will look like a hanging range and get unhung later--which will take the break we want to remove out of the range
            // so to avoid this we collapse the target range to default to single character deletion
            if (direction === 'backward') {
              targetRange = {anchor: end, focus: end}
              start = end
              leaf = nextEntry.node
              path = nextEntry.path
            } else {
              targetRange = {anchor: start, focus: start}
              end = start
            }
          }
        }
      }

      if (!isSpan({schema: editor.schema}, leaf)) {
        return
      }

      const diff = {
        text: '',
        start: start.offset,
        end: end.offset,
      }
      const pendingDiffs = editor.pendingDiffs
      const relevantPendingDiffs = pendingDiffs?.find((change) =>
        pathEquals(change.path, path),
      )
      const diffs = relevantPendingDiffs
        ? [relevantPendingDiffs.diff, diff]
        : [diff]
      const text = applyStringDiff(leaf.text, ...diffs)

      if (text.length === 0) {
        // Text leaf will be removed, so we need to schedule an
        // action to remove it so that Slate can normalize instead
        // of storing as a diff
        canStoreDiff = false
      }

      if (isExpandedRange(targetRange)) {
        if (
          canStoreDiff &&
          pathEquals(targetRange.anchor.path, targetRange.focus.path)
        ) {
          const point = {path: targetRange.anchor.path, offset: start.offset}
          const range = editorRange(editor, point, point)
          handleUserSelect(range)

          return storeDiff(targetRange.anchor.path, {
            text: '',
            end: end.offset,
            start: start.offset,
          })
        }

        return scheduleAction(
          () =>
            editorActor.send({
              type: 'behavior event',
              behaviorEvent: {type: 'delete', direction},
              editor,
            }),
          {at: targetRange},
        )
      }
    }

    switch (type) {
      case 'deleteByComposition':
      case 'deleteByCut':
      case 'deleteByDrag': {
        return scheduleAction(
          () =>
            editorActor.send({
              type: 'behavior event',
              behaviorEvent: {type: 'delete', direction: 'forward'},
              editor,
            }),
          {at: targetRange},
        )
      }

      case 'deleteContent':
      case 'deleteContentForward': {
        const {anchor} = targetRange
        if (canStoreDiff && isCollapsedRange(targetRange)) {
          const targetNodeEntry = getSpanNode(editor, anchor.path)

          if (
            targetNodeEntry &&
            anchor.offset < targetNodeEntry.node.text.length
          ) {
            return storeDiff(anchor.path, {
              text: '',
              start: anchor.offset,
              end: anchor.offset + 1,
            })
          }
        }

        return scheduleAction(
          () =>
            editorActor.send({
              type: 'behavior event',
              behaviorEvent: {type: 'delete.forward', unit: 'character'},
              editor,
            }),
          {at: targetRange},
        )
      }

      case 'deleteContentBackward': {
        const {anchor} = targetRange

        // If we have a mismatch between the native and slate selection being collapsed
        // we are most likely deleting a zero-width placeholder and thus should perform it
        // as an action to ensure correct behavior (mostly happens with mark placeholders)
        const nativeCollapsed = isDOMSelection(nativeTargetRange)
          ? nativeTargetRange.isCollapsed
          : !!nativeTargetRange?.collapsed

        if (
          canStoreDiff &&
          nativeCollapsed &&
          isCollapsedRange(targetRange) &&
          anchor.offset > 0
        ) {
          return storeDiff(anchor.path, {
            text: '',
            start: anchor.offset - 1,
            end: anchor.offset,
          })
        }

        return scheduleAction(
          () =>
            editorActor.send({
              type: 'behavior event',
              behaviorEvent: {type: 'delete.backward', unit: 'character'},
              editor,
            }),
          {at: targetRange},
        )
      }

      case 'deleteEntireSoftLine': {
        return scheduleAction(
          () => {
            editorActor.send({
              type: 'behavior event',
              behaviorEvent: {type: 'delete.backward', unit: 'line'},
              editor,
            })
            editorActor.send({
              type: 'behavior event',
              behaviorEvent: {type: 'delete.forward', unit: 'line'},
              editor,
            })
          },
          {at: targetRange},
        )
      }

      case 'deleteHardLineBackward': {
        return scheduleAction(
          () =>
            editorActor.send({
              type: 'behavior event',
              behaviorEvent: {type: 'delete.backward', unit: 'block'},
              editor,
            }),
          {at: targetRange},
        )
      }

      case 'deleteSoftLineBackward': {
        return scheduleAction(
          () =>
            editorActor.send({
              type: 'behavior event',
              behaviorEvent: {type: 'delete.backward', unit: 'line'},
              editor,
            }),
          {at: targetRange},
        )
      }

      case 'deleteHardLineForward': {
        return scheduleAction(
          () =>
            editorActor.send({
              type: 'behavior event',
              behaviorEvent: {type: 'delete.forward', unit: 'block'},
              editor,
            }),
          {at: targetRange},
        )
      }

      case 'deleteSoftLineForward': {
        return scheduleAction(
          () =>
            editorActor.send({
              type: 'behavior event',
              behaviorEvent: {type: 'delete.forward', unit: 'line'},
              editor,
            }),
          {at: targetRange},
        )
      }

      case 'deleteWordBackward': {
        return scheduleAction(
          () =>
            editorActor.send({
              type: 'behavior event',
              behaviorEvent: {type: 'delete.backward', unit: 'word'},
              editor,
            }),
          {at: targetRange},
        )
      }

      case 'deleteWordForward': {
        return scheduleAction(
          () =>
            editorActor.send({
              type: 'behavior event',
              behaviorEvent: {type: 'delete.forward', unit: 'word'},
              editor,
            }),
          {at: targetRange},
        )
      }

      case 'insertLineBreak': {
        return scheduleAction(
          () =>
            editorActor.send({
              type: 'behavior event',
              behaviorEvent: {type: 'insert.soft break'},
              editor,
            }),
          {at: targetRange},
        )
      }

      case 'insertParagraph': {
        return scheduleAction(
          () =>
            editorActor.send({
              type: 'behavior event',
              behaviorEvent: {type: 'insert.break'},
              editor,
            }),
          {at: targetRange},
        )
      }
      case 'insertCompositionText':
      case 'deleteCompositionText':
      case 'insertFromComposition':
      case 'insertFromDrop':
      case 'insertFromPaste':
      case 'insertFromYank':
      case 'insertReplacementText':
      case 'insertText': {
        if (isDataTransfer(data)) {
          return scheduleAction(
            () =>
              editorActor.send({
                type: 'behavior event',
                behaviorEvent: {
                  type: 'input.*',
                  originEvent: {dataTransfer: data},
                },
                editor,
              }),
            {at: targetRange},
          )
        }

        let text = data ?? ''

        // Pastes from the Android clipboard will generate `insertText` events.
        // If the copied text contains any newlines, Android will append an
        // extra newline to the end of the copied text.
        if (type === 'insertText' && /.*\n.*\n$/.test(text)) {
          text = text.slice(0, -1)
        }

        // If the text includes a newline, split it at newlines and paste each component
        // string, with soft breaks in between each.
        if (text.includes('\n')) {
          return scheduleAction(
            () => {
              const parts = text.split('\n')
              parts.forEach((line, i) => {
                if (line) {
                  editorActor.send({
                    type: 'behavior event',
                    behaviorEvent: {type: 'insert.text', text: line},
                    editor,
                  })
                }
                if (i !== parts.length - 1) {
                  editorActor.send({
                    type: 'behavior event',
                    behaviorEvent: {type: 'insert.soft break'},
                    editor,
                  })
                }
              })
            },
            {
              at: targetRange,
            },
          )
        }

        if (pathEquals(targetRange.anchor.path, targetRange.focus.path)) {
          const [start, end] = rangeEdges(targetRange, editor)

          const diff = {
            start: start.offset,
            end: end.offset,
            text,
          }

          // COMPAT: Swiftkey has a weird bug where the target range of the 2nd word
          // inserted after a mark placeholder is inserted with an anchor offset off by 1.
          // So writing 'some text' will result in 'some ttext'. Luckily all 'normal' insert
          // text events are fired with the correct target ranges, only the final 'insertComposition'
          // isn't, so we can adjust the target range start offset if we are confident this is the
          // swiftkey insert causing the issue.
          if (text && insertPositionHint && type === 'insertCompositionText') {
            const hintPosition =
              insertPositionHint.start + insertPositionHint.text.search(/\S|$/)
            const diffPosition = diff.start + diff.text.search(/\S|$/)

            if (
              diffPosition === hintPosition + 1 &&
              diff.end ===
                insertPositionHint.start + insertPositionHint.text.length
            ) {
              debug('adjusting swiftKey insert position using hint')
              diff.start -= 1
              insertPositionHint = null
              scheduleFlush()
            } else {
              insertPositionHint = false
            }
          } else if (type === 'insertText') {
            if (insertPositionHint === null) {
              insertPositionHint = diff
            } else if (
              insertPositionHint &&
              isCollapsedRange(targetRange) &&
              insertPositionHint.end + insertPositionHint.text.length ===
                start.offset
            ) {
              insertPositionHint = {
                ...insertPositionHint,
                text: insertPositionHint.text + text,
              }
            } else {
              insertPositionHint = false
            }
          } else {
            insertPositionHint = false
          }

          if (canStoreDiff) {
            const currentSelection = editor.selection
            storeDiff(start.path, diff)

            if (currentSelection) {
              const newPoint = {
                path: start.path,
                offset: start.offset + text.length,
              }

              scheduleAction(
                () => {
                  editor.select({
                    anchor: newPoint,
                    focus: newPoint,
                  })
                },
                {at: newPoint},
              )
            }
            return
          }
        }

        return scheduleAction(
          () =>
            editorActor.send({
              type: 'behavior event',
              behaviorEvent: {type: 'insert.text', text},
              editor,
            }),
          {at: targetRange},
        )
      }
    }
  }

  const hasPendingAction = () => {
    return !!editor.pendingAction
  }

  const hasPendingDiffs = () => {
    return !!editor.pendingDiffs?.length
  }

  const hasPendingChanges = () => {
    return hasPendingAction() || hasPendingDiffs()
  }

  const isFlushing = () => {
    return flushing
  }

  const handleUserSelect = (range: Range | null) => {
    editor.pendingSelection = range

    if (flushTimeoutId) {
      clearTimeout(flushTimeoutId)
      flushTimeoutId = null
    }

    const {selection} = editor
    if (!range) {
      return
    }

    const pathChanged =
      !selection || !pathEquals(selection.anchor.path, range.anchor.path)
    const parentPathChanged =
      !selection ||
      !pathEquals(
        selection.anchor.path.slice(0, -1),
        range.anchor.path.slice(0, -1),
      )

    if ((pathChanged && insertPositionHint) || parentPathChanged) {
      insertPositionHint = false
    }

    if (pathChanged || hasPendingDiffs()) {
      flushTimeoutId = setTimeout(flush, FLUSH_DELAY)
    }
  }

  const handleInput = () => {
    if (hasPendingAction() || !hasPendingDiffs()) {
      debug('flush input')
      flush()
    }
  }

  const handleKeyDown = (_: React.KeyboardEvent) => {}

  const scheduleFlush = () => {
    if (!hasPendingAction()) {
      actionTimeoutId = setTimeout(flush)
    }
  }

  const handleDomMutations = (mutations: MutationRecord[]) => {
    if (hasPendingDiffs() || hasPendingAction()) {
      return
    }

    if (
      mutations.some((mutation) =>
        isTrackedMutation(editor, mutation, mutations),
      )
    ) {
      // Cause a re-render to restore the dom state if we encounter tracked mutations without
      // a corresponding pending action.
      editor.forceRender?.()
    }
  }

  return {
    flush,
    scheduleFlush,

    hasPendingDiffs,
    hasPendingAction,
    hasPendingChanges,

    isFlushing,

    handleUserSelect,
    handleCompositionEnd,
    handleCompositionStart,
    handleDOMBeforeInput,
    handleKeyDown,

    handleDomMutations,
    handleInput,
  }
}
